// src/pages/SettingsPage/components/WorkspaceAdminPaymentSettings.tsx
import { useAuth0 } from "@auth0/auth0-react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import {
  useFetchPaymentMethods,
  usePaymentMethods,
  useFetchStripeSubscription,
} from "../../../hook/settings";
import { stripePromise } from "../../../stripe";
import { formatDate } from "./utils/settings.utils";
import { AddPaymentMethodModal } from "./AddPaymentMethodModal";
import {
  deletePaymentMethod,
  setDefaultPaymentMethod,
} from "../../../api/payment";
import { NoteBlockContainer } from "../../note";
import { StaticHtmlBlock } from "../../staticHtmlBlock";

export const WorkspaceAdminPaymentSettings: React.FC = () => {
  const fetchPaymentMethodsHook = useFetchPaymentMethods(); // Renamed
  const [paymentMethods, setPaymentMethods] = usePaymentMethods();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const isDefaultMethod = (method: any) =>
    method["stripe-id"] === paymentMethods?.["default-payment-method"];
  const { user } = useAuth0();
  const email = user?.email!;
  const fetchSubscription = useFetchStripeSubscription();

  const onSetPaymentMethod = (id: string) => {
    setDefaultPaymentMethod({ paymentMethod: id, email }).then(() => {
      fetchPaymentMethodsHook(); // Use renamed hook
      fetchSubscription();
    });
  };

  const onDeletePaymentMethod = (id: string) => {
    deletePaymentMethod({ paymentMethod: id }).then(() => {
      fetchPaymentMethodsHook(); // Use renamed hook
      fetchSubscription();
    });
  };

  return (
    <>
      {/* Wrap Modal in Elements for Stripe context */}
      <Elements stripe={stripePromise}>
        <AddPaymentMethodModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
        />
      </Elements>
      <NoteBlockContainer name="Payment Methods">
        <StaticHtmlBlock html="<p>Add a card to enable paid access after your trial. Your default payment method is charged at each renewal.</p>" />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Last 4</TableCell>
                <TableCell>Expiration</TableCell>
                <TableCell>Default</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentMethods?.methods?.map((paymentMethod, i) => {
                const isDefault = isDefaultMethod(paymentMethod);
                return (
                  <TableRow key={paymentMethod["stripe-id"]}>
                    {" "}
                    {/* Use stripe-id for key */}
                    <TableCell>
                      {formatDate({ timestamp: paymentMethod.created })}
                    </TableCell>
                    <TableCell>{paymentMethod.brand}</TableCell>
                    <TableCell>{paymentMethod.last4}</TableCell>
                    <TableCell>{`${paymentMethod["exp-month"]}/${paymentMethod["exp-year"]}`}</TableCell>{" "}
                    {/* Use / separator */}
                    <TableCell>
                      <Checkbox
                        checked={isDefault}
                        disabled={isDefault}
                        // sx={{ color: theme.palette.primary.main }} // Color only if needed
                        onClick={() =>
                          onSetPaymentMethod(paymentMethod["stripe-id"])
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        disabled={isDefault}
                        sx={{
                          color: isDefault
                            ? theme.palette.action.disabled
                            : theme.palette.error.main,
                        }} // Error color for delete
                        onClick={() =>
                          onDeletePaymentMethod(paymentMethod["stripe-id"])
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Box sx={{ justifyContent: "end", display: "flex", pt: 1 }}>
            {" "}
            {/* Add padding top */}
            <IconButton
              onClick={() => {
                setModalOpen(true);
              }}
              sx={{ color: theme.palette.primary.main }}
              aria-label="Add payment method" // Add aria-label
            >
              <AddIcon />
            </IconButton>
          </Box>
        </TableContainer>
      </NoteBlockContainer>
    </>
  );
};
